import { Button, TextInput } from "@mantine/core";
import { useState } from "react";
import { useNavigate } from "react-router";
import { authClient } from "../../auth/client";
import { useAuth } from "../../auth/provider";
import { useMessages } from "../../i18n/provider";
import styles from "./auth.module.css";
import { AuthFooterLink, AuthLayout } from "./layout";
import { SocialButtons } from "./social-buttons";

export function SignUpPage() {
  const t = useMessages();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (name.trim().length === 0) {
      setError(t("auth.nameRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("auth.invalidEmail"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.passwordShort"));
      return;
    }
    setLoading(true);
    const result = await authClient.signUp.email({
      email,
      password,
      name: name.trim(),
    });
    setLoading(false);
    if (result.error) {
      if (result.error.status === 429) {
        setError(t("auth.tooManyAttempts"));
      } else if (
        result.error.code === "USER_ALREADY_EXISTS" ||
        result.error.code === "EMAIL_ALREADY_EXISTS"
      ) {
        setError(t("auth.emailTaken"));
      } else {
        setError(t("auth.genericError"));
      }
      return;
    }
    await refresh();
    navigate("/dashboard", { replace: true });
  };

  return (
    <AuthLayout
      title={t("auth.createAccount")}
      footer={
        <>
          {t("auth.haveAccount")} <AuthFooterLink to="/login" label={t("auth.signIn")} />
        </>
      }
    >
      <SocialButtons next="/dashboard" />
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      <form className={styles.form} onSubmit={(event) => void submit(event)}>
        <TextInput
          label={t("auth.name")}
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          required
        />
        <TextInput
          label={t("auth.email")}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          required
        />
        <TextInput
          label={t("auth.password")}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
          required
          description={t("auth.passwordHint")}
        />
        <Button type="submit" fullWidth loading={loading} mt={4}>
          {t("auth.signUp")}
        </Button>
      </form>
    </AuthLayout>
  );
}
